/**
 * Utility for optimizing CSS loading
 */

// Font loading optimization
const FONT_WEIGHTS = [300, 400, 500, 600, 700];
const FONT_FAMILIES = ['Heebo', 'Varela Round'];

/**
 * Preloads critical web fonts asynchronously
 * @param {Array} fonts - Array of font families to preload
 * @param {Array} weights - Array of font weights to preload
 */
export function preloadCriticalFonts(fonts = FONT_FAMILIES, weights = FONT_WEIGHTS) {
  if (!fonts || !fonts.length) return;

  // Create a special font observer
  const fontObserver = new FontFaceObserver();

  // Mark fonts as loading
  document.documentElement.classList.add('fonts-loading');

  // Create promises for each font to load
  const fontPromises = [];

  fonts.forEach(family => {
    // Only load weights we need
    weights.forEach(weight => {
      fontPromises.push(
        fontObserver
          .load({
            family: family,
            weight: weight,
            timeout: 3000 // Set a reasonable timeout
          })
          .catch(err => {
            console.warn(`Failed to load font ${family} (weight: ${weight}):`, err);
          })
      );
    });
  });

  // When all fonts are loaded (or timed out)
  Promise.all(fontPromises)
    .then(() => {
      // Mark fonts as loaded in the DOM
      document.documentElement.classList.remove('fonts-loading');
      document.documentElement.classList.add('fonts-loaded');
      
      // Store font loaded state in session storage to prevent FOUT on page navigation
      try {
        sessionStorage.setItem('fonts-loaded', 'true');
      } catch (e) {
        // Session storage not available
      }
    })
    .catch(err => {
      console.error('Error loading fonts:', err);
      // Remove loading class to unblock rendering even if fonts failed
      document.documentElement.classList.remove('fonts-loading');
    });
}

/**
 * Font Face Observer - simplified version
 * Helps monitor when web fonts have loaded
 */
function FontFaceObserver() {
  return {
    load: function(options) {
      const family = options.family || 'sans-serif';
      const weight = options.weight || 'normal';
      const timeout = options.timeout || 3000;
      
      return new Promise((resolve, reject) => {
        // Create a test element
        const testElement = document.createElement('span');
        testElement.textContent = 'BESbswy';
        testElement.style.position = 'absolute';
        testElement.style.left = '-10000px';
        testElement.style.top = '-10000px';
        testElement.style.fontSize = '16px';
        testElement.style.fontFamily = 'sans-serif';
        testElement.style.fontWeight = weight;
        document.body.appendChild(testElement);
        
        // Get the initial dimensions
        const initialWidth = testElement.offsetWidth;
        
        // Apply the test font
        testElement.style.fontFamily = `${family}, sans-serif`;
        
        // Start time for timeout
        const startTime = Date.now();
        
        // Check if the font has loaded
        const checkFont = () => {
          // Check if timeout has occurred
          if (Date.now() - startTime > timeout) {
            document.body.removeChild(testElement);
            reject(new Error(`Font loading timed out: ${family}`));
            return;
          }
          
          // If the width changed, the font has loaded
          if (testElement.offsetWidth !== initialWidth) {
            document.body.removeChild(testElement);
            resolve();
            return;
          }
          
          // Check again
          requestAnimationFrame(checkFont);
        };
        
        requestAnimationFrame(checkFont);
      });
    }
  };
}

/**
 * Loads CSS file asynchronously
 * @param {string} href - URL of the CSS file to load
 * @returns {Promise} - Resolves when the CSS is loaded
 */
export function loadCSS(href) {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Failed to load CSS: ${href}`));
    
    document.head.appendChild(link);
  });
}

export default {
  preloadCriticalFonts,
  loadCSS
}; 